import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import Icon from './Icon';
import sonaeAiLogo from '../assets/images/logo/Service.png';
import toshoLogo from '../assets/images/logo/Company.png';

interface BCPPageHeaderProps {
  userName?: string;
  // BCP編集/作成ページ用のプロパティ
  showActions?: boolean;
  showBackButton?: boolean;
  isEditMode?: boolean;
  onSave?: () => void | Promise<void>;
  onDownload?: () => void | Promise<void>;
  onBackToTop?: () => void;
  saveLoading?: boolean;
  downloadLoading?: boolean;
  saveDisabled?: boolean;
  downloadDisabled?: boolean;
  menuItems?: HeaderMenuItem[];
  menuLabel?: string;
}

export interface HeaderMenuItem {
  id: string;
  label: string;
  description?: string;
  onSelect: () => void;
}

const BCPPageHeader: React.FC<BCPPageHeaderProps> = ({
  showBackButton = true,
  onSave,
  onDownload,
  onBackToTop,
  saveLoading = false,
  downloadLoading = false,
  saveDisabled = false,
  downloadDisabled = false,
  menuItems = [],
  menuLabel = 'メニュー',
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleBackToTop = () => {
    if (onBackToTop) {
      onBackToTop();
    } else {
      void navigate('/');
    }
  };

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const handleMenuItemSelect = (item: HeaderMenuItem) => {
    setIsMenuOpen(false);
    item.onSelect();
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full" style={{ padding: '14px' }}>
        <div className="flex justify-between items-center" style={{ minHeight: '36px' }}>
          {/* 左側: ロゴ */}
          <div className="flex items-center justify-between" style={{ width: '317px', height: '32px', opacity: 1 }}>
            <img
              src={sonaeAiLogo}
              alt="SONAE-AI"
              style={{ width: '187px', height: '57px', opacity: 1, cursor: 'pointer' }}
              onClick={handleBackToTop}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <img
              src={toshoLogo}
              alt="東京商工会議所"
              style={{ width: '128px', height: '32px', opacity: 1 }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>

          {/* 右側: アクションボタン */}
          <div className="flex items-center gap-3">
            {menuItems.length > 0 && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen}
                >
                  <Icon type="menu" size="sm" className="text-gray-600" />
                  <span className="font-medium">{menuLabel}</span>
                  <Icon
                    type="chevron-down"
                    size="sm"
                    className={`text-gray-500 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isMenuOpen && (
                  <div className="absolute right-0 z-[60] mt-2 w-64 rounded-md border border-gray-200 bg-white py-2 shadow-lg">
                    {menuItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleMenuItemSelect(item)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                      >
                        <span className="block font-medium">{item.label}</span>
                        {item.description && (
                          <span className="mt-0.5 block text-xs text-gray-500">{item.description}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TOPへ戻るボタン */}
            {showBackButton && (
              <Button
                variant="secondary"
                onClick={handleBackToTop}
              >
                TOPへ戻る
              </Button>
            )}

            {/* 保存ボタン */}
            {onSave && (
              <Button
                variant="secondary"
                onClick={() => {
                  void onSave();
                }}
                loading={saveLoading}
                disabled={saveLoading || downloadLoading || saveDisabled}
              >
                保存する
              </Button>
            )}

            {/* ダウンロードボタン */}
            {onDownload && (
              <Button
                variant="primary"
                onClick={() => {
                  void onDownload();
                }}
                loading={downloadLoading}
                disabled={saveLoading || downloadLoading || downloadDisabled}
              >
                ダウンロード
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default BCPPageHeader;
